import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Eye, Users, Trophy, TrendingUp } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const FLAG_URL = (code) => `https://flagcdn.com/w80/${code?.toLowerCase()}.png`;

// Función para calcular puntos
const calculatePoints = (prediction, result) => {
  if (!prediction || !result) return 0;
  
  const { home_goals: predHome, away_goals: predAway } = prediction;
  const { home_goals: resHome, away_goals: resAway } = result;
  
  // 3 puntos por marcador exacto
  if (predHome === resHome && predAway === resAway) {
    return 3;
  }
  
  // 1 punto por resultado correcto (ganador o empate)
  const predResult = predHome > predAway ? 'home' : predHome < predAway ? 'away' : 'draw';
  const actualResult = resHome > resAway ? 'home' : resHome < resAway ? 'away' : 'draw';
  
  if (predResult === actualResult) {
    return 1;
  }
  
  return 0;
};

export default function PredictionsAdmin() {
  const [matches, setMatches] = useState([]);
  const [results, setResults] = useState({});
  const [stages, setStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('finalizado');
  const [loading, setLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [matchPredictions, setMatchPredictions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loadingPredictions, setLoadingPredictions] = useState(false);

  useEffect(() => {
    loadMatches();
  }, []);

  const loadMatches = async () => {
    try {
      const [m, r, s] = await Promise.all([
        api.get('/matches/enriched'),
        api.get('/results'),
        api.get('/stages')
      ]);

      // Ordenar cronológicamente (más recientes primero)
      const sorted = [...m.data].sort((a, b) => {
        const dateA = new Date(`${a.date}T${a.time}`);
        const dateB = new Date(`${b.date}T${b.time}`);
        return dateB - dateA;
      });

      setMatches(sorted);
      setStages(s.data);

      // Mapear resultados
      const resultsMap = {};
      r.data.forEach(res => {
        resultsMap[res.match_id] = { home_goals: res.home_goals, away_goals: res.away_goals };
      });
      setResults(resultsMap);
    } finally {
      setLoading(false);
    }
  };

  const handleMatchClick = async (match) => {
    setSelectedMatch(match);
    setShowModal(true);
    setLoadingPredictions(true);
    
    try {
      // Obtener TODAS las predicciones de todos los usuarios (endpoint admin)
      const [predictions, users] = await Promise.all([
        api.get('/predictions/all'), // Cambio: usar /predictions/all para obtener todas las predicciones
        api.get('/users')
      ]);
      
      // Filtrar predicciones de este partido y enriquecer con datos de usuario
      const matchPreds = predictions.data
        .filter(pred => pred.match_id === match.id)
        .map(pred => {
          const user = users.data.find(u => u.id === pred.user_id);
          const result = results[match.id];
          const points = calculatePoints(pred, result);
          
          return {
            ...pred,
            user_id_display: user?.user_id || pred.user_id,
            points
          };
        })
        .sort((a, b) => b.points - a.points); // Ordenar por puntos (mayor primero)
      
      setMatchPredictions(matchPreds);
    } catch (err) {
      console.error('Error loading predictions:', err);
      setMatchPredictions([]);
    } finally {
      setLoadingPredictions(false);
    }
  };

  // Filtrar por etapa y estado
  let filtered = matches;
  if (selectedStage !== 'all') {
    filtered = filtered.filter(m => m.stage?.id === selectedStage);
  }
  if (selectedStatus !== 'all') {
    filtered = filtered.filter(m => m.status === selectedStatus);
  }

  return (
    <div className="space-y-4 animate-fade-in" data-testid="admin-predictions-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-black gold-text text-center lg:text-left" style={{ fontFamily: 'Montserrat' }}>
            Predicciones de Usuarios
          </h2>
          <p className="text-white/50 text-sm mt-1">Consulta las predicciones de los partidos</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-white/50 text-xs uppercase tracking-wider">Estado del partido</label>
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger className="bg-[#111A30] border-[#F4E39E]/30 text-white mt-1" data-testid="predictions-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#111A30] border-[#F4E39E]/30">
              <SelectItem value="all" className="text-white">Todos los partidos</SelectItem>
              <SelectItem value="pendiente" className="text-white">Pendientes por jugar</SelectItem>
              <SelectItem value="finalizado" className="text-white">Finalizados</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-white/50 text-xs uppercase tracking-wider">Etapa</label>
          <Select value={selectedStage} onValueChange={setSelectedStage}>
            <SelectTrigger className="bg-[#111A30] border-[#F4E39E]/30 text-white mt-1" data-testid="stage-filter">
              <SelectValue placeholder="Todas las etapas" />
            </SelectTrigger>
            <SelectContent className="bg-[#111A30] border-[#F4E39E]/30">
              <SelectItem value="all" className="text-white">Todas las etapas</SelectItem>
              {stages.map(s => (
                <SelectItem key={s.id} value={s.id} className="text-white">{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-white/50 py-10">Cargando partidos...</div>
      ) : filtered.length === 0 ? (
        <div className="glass-card p-8 text-center text-white/40">
          No hay partidos que coincidan con los filtros seleccionados
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((match) => {
            const result = results[match.id];
            const isFinalized = match.status === 'finalizado';

            return (
              <div
                key={match.id}
                className="glass-card p-4 cursor-pointer hover:border-[#F4E39E]/50 transition-all hover:shadow-lg"
                onClick={() => handleMatchClick(match)}
                data-testid={`match-card-${match.id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-[#F4E39E] font-semibold">{match.stage?.name}</span>
                  <Badge className={`text-[10px] ${isFinalized ? 'bg-green-500/20 text-green-400' : 'bg-[#F4E39E]/20 text-[#F4E39E]'}`}>
                    {isFinalized ? 'FINALIZADO' : 'PENDIENTE'}
                  </Badge>
                </div>

                {/* Marcador */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 flex-1">
                    <img src={FLAG_URL(match.home_team?.flag_code)} alt="" className="flag-img" onError={(e) => e.target.style.display='none'} />
                    <span className="text-white font-bold text-sm">{match.home_team?.name}</span>
                  </div>

                  <div className="flex items-center gap-2 px-3">
                    <span className="text-white font-black text-xl" style={{ fontFamily: 'Montserrat' }}>
                      {result ? result.home_goals : '-'}
                    </span>
                    <span className="text-white/30">-</span>
                    <span className="text-white font-black text-xl" style={{ fontFamily: 'Montserrat' }}>
                      {result ? result.away_goals : '-'}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 mb-3">
                  <img src={FLAG_URL(match.away_team?.flag_code)} alt="" className="flag-img" onError={(e) => e.target.style.display='none'} />
                  <span className="text-white font-bold text-sm">{match.away_team?.name}</span>
                </div>

                <div className="text-white/40 text-xs text-center mb-3">
                  {match.date} - {match.time}
                </div>

                <button className="w-full bg-[#F4E39E]/10 hover:bg-[#F4E39E]/20 border border-[#F4E39E]/30 text-[#F4E39E] py-2 px-3 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm font-semibold">
                  <Eye size={16} />
                  Ver Predicciones
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de Predicciones */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#111A30] border-[#F4E39E]/30 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#F4E39E] flex items-center gap-2" style={{ fontFamily: 'Montserrat' }}>
              <Users size={20} />
              Predicciones de Usuarios
            </DialogTitle>
          </DialogHeader>

          {selectedMatch && (
            <div className="space-y-4">
              {/* Info del partido */}
              <div className="bg-[#0A0F1C] rounded-lg p-4 border border-[#F4E39E]/20">
                <p className="text-[#F4E39E] text-sm font-semibold mb-2">{selectedMatch.stage?.name}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img src={FLAG_URL(selectedMatch.home_team?.flag_code)} alt="" className="flag-img" onError={(e) => e.target.style.display='none'} />
                    <span className="text-white font-bold">{selectedMatch.home_team?.name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-white font-black text-2xl" style={{ fontFamily: 'Montserrat' }}>
                      {results[selectedMatch.id]?.home_goals ?? '-'}
                    </span>
                    <span className="text-white/30">-</span>
                    <span className="text-white font-black text-2xl" style={{ fontFamily: 'Montserrat' }}>
                      {results[selectedMatch.id]?.away_goals ?? '-'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold">{selectedMatch.away_team?.name}</span>
                    <img src={FLAG_URL(selectedMatch.away_team?.flag_code)} alt="" className="flag-img" onError={(e) => e.target.style.display='none'} />
                  </div>
                </div>
                <p className="text-white/50 text-xs text-center mt-2">{selectedMatch.date} - {selectedMatch.time}</p>
              </div>

              {/* Lista de predicciones */}
              {loadingPredictions ? (
                <div className="text-center text-white/50 py-6">Cargando predicciones...</div>
              ) : matchPredictions.length === 0 ? (
                <div className="bg-[#0A0F1C]/50 rounded-lg p-6 text-center border border-white/10">
                  <p className="text-white/40">No hay predicciones para este partido</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-white/50 text-xs font-semibold px-3">
                    <span className="flex-1">Usuario</span>
                    <span className="w-24 text-center">Predicción</span>
                    <span className="w-20 text-right">Puntos</span>
                  </div>
                  
                  {matchPredictions.map((pred, idx) => (
                    <div
                      key={pred.id || idx}
                      className={`glass-card p-3 flex items-center justify-between ${
                        pred.points === 3 ? 'border-green-500/50 bg-green-500/5' : 
                        pred.points === 1 ? 'border-blue-500/50 bg-blue-500/5' : ''
                      }`}
                    >
                      <div className="flex items-center gap-2 flex-1">
                        <div className="w-8 h-8 rounded-full bg-[#F4E39E]/20 flex items-center justify-center text-[#F4E39E] text-xs font-bold">
                          {idx + 1}
                        </div>
                        <span className="text-white font-semibold text-sm">{pred.user_id_display}</span>
                      </div>
                      
                      <div className="flex items-center gap-2 w-24 justify-center">
                        <span className="text-white font-bold text-lg">{pred.home_goals}</span>
                        <span className="text-white/30">-</span>
                        <span className="text-white font-bold text-lg">{pred.away_goals}</span>
                      </div>
                      
                      <div className="w-20 text-right">
                        <Badge className={`
                          ${pred.points === 3 ? 'bg-green-500/20 text-green-400' : 
                            pred.points === 1 ? 'bg-blue-500/20 text-blue-400' : 
                            'bg-white/10 text-white/50'}
                        `}>
                          {pred.points === 3 && <Trophy size={12} className="inline mr-1" />}
                          {pred.points === 1 && <TrendingUp size={12} className="inline mr-1" />}
                          {pred.points} pts
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Resumen */}
              {matchPredictions.length > 0 && (
                <div className="bg-[#F4E39E]/10 rounded-lg p-4 border border-[#F4E39E]/30">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-white/50 text-xs">Total</p>
                      <p className="text-white font-bold text-lg">{matchPredictions.length}</p>
                    </div>
                    <div>
                      <p className="text-white/50 text-xs">Exactos (3pts)</p>
                      <p className="text-green-400 font-bold text-lg">
                        {matchPredictions.filter(p => p.points === 3).length}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/50 text-xs">Correctos (1pt)</p>
                      <p className="text-blue-400 font-bold text-lg">
                        {matchPredictions.filter(p => p.points === 1).length}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
